import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Chip, 
  Button,
  CircularProgress,
  LinearProgress,
  Alert
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { ResponsiveRadar } from '@nivo/radar';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveLine } from '@nivo/line';
import { useNavigate } from 'react-router-dom';

import { 
  useGetResilienceScoreQuery, 
  useGetSustainabilityMetricsQuery,
  useGetActiveDisruptionsQuery,
  useGetRecommendationsQuery,
  useGetAgentStatusQuery,
  useGetTaskQueueQuery,
  useGetMemoryStatsQuery,
  useGetTaskHistoryQuery
} from '../store/api';

// Import Dope components for digital dopamine
import {
  SupplyChainHealthScore,
  ModeToggle,
  useConfetti,
  SmartQuestionChips,
  ProgressSystem,
} from '../components/dope';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // Confetti celebration hook
  const { celebrate, ConfettiComponent } = useConfetti();
  
  // Track login streak (would come from backend in production)
  const [loginStreak, setLoginStreak] = useState(5);
  const [currentXP, setCurrentXP] = useState(250);
  
  // Fetch data from API
  const { data: resilienceScore, isLoading: isLoadingResilience } = useGetResilienceScoreQuery();
  const { data: sustainabilityMetrics, isLoading: isLoadingSustainability } = useGetSustainabilityMetricsQuery();
  const { data: activeDisruptions, isLoading: isLoadingDisruptions } = useGetActiveDisruptionsQuery();
  const { data: recommendations, isLoading: isLoadingRecommendations } = useGetRecommendationsQuery();
  
  // Fetch agent data from API
  const { data: agentStatus, isLoading: isLoadingAgentStatus, error: agentStatusError } = useGetAgentStatusQuery();
  const { data: taskQueue, isLoading: isLoadingTaskQueue, error: taskQueueError } = useGetTaskQueueQuery({ limit: 5 });
  const { data: memoryStats, isLoading: isLoadingMemoryStats, error: memoryStatsError } = useGetMemoryStatsQuery();
  const { data: taskHistory, isLoading: isLoadingTaskHistory, error: taskHistoryError } = useGetTaskHistoryQuery({ limit: 10 });

  // Handle smart question click - navigate to chat with the question
  const handleQuestionClick = (question: string) => {
    navigate('/chat', { state: { prefillQuestion: question } });
  };

  // Prepare data for resilience radar chart
  const getResilienceRadarData = () => {
    if (!resilienceScore || !resilienceScore.component_scores) return [];
    
    return Object.entries(resilienceScore.component_scores).map(([key, value]) => ({
      metric: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: value
    }));
  };

  // Prepare data for carbon footprint pie chart
  const getCarbonFootprintData = () => {
    if (!sustainabilityMetrics || !sustainabilityMetrics.carbon_footprint.breakdown) return [];
    
    return Object.entries(sustainabilityMetrics.carbon_footprint.breakdown).map(([key, value]) => ({
      id: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      label: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: value
    }));
  };

  // Prepare data for carbon footprint trend line chart
  const getCarbonTrendData = () => {
    if (!sustainabilityMetrics || !sustainabilityMetrics.carbon_footprint.historical) return [];
    
    return [
      {
        id: 'Carbon Emissions',
        data: sustainabilityMetrics.carbon_footprint.historical.map(item => ({
          x: item.period,
          y: item.value
        }))
      }
    ];
  };

  return (
    <Box>
      {/* Confetti celebration overlay */}
      {ConfettiComponent}
      
      {/* Header with Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Supply Chain Intelligence
        </Typography>
        <ModeToggle />
      </Box>

      {/* Smart Question Chips - AI-suggested questions */}
      <SmartQuestionChips
        resilienceScore={resilienceScore?.overall_resilience_score}
        carbonTrend={sustainabilityMetrics?.carbon_footprint.trend || 0}
        activeDisruptions={activeDisruptions?.length || 0}
        onQuestionClick={handleQuestionClick}
        isLoading={isLoadingResilience || isLoadingSustainability || isLoadingDisruptions}
      />

      {/* Main Health Score + Progress Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <SupplyChainHealthScore
            resilienceScore={resilienceScore?.overall_resilience_score || 0}
            carbonReduction={sustainabilityMetrics?.carbon_footprint.trend || 0}
            supplierHealth={75} // Would come from supplier API
            inventoryEfficiency={82} // Would come from inventory API
            isLoading={isLoadingResilience || isLoadingSustainability}
            previousScore={68} // Would track from previous period
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgressSystem
            currentXP={currentXP}
            completedMilestones={[
              'Connect your first data source',
              'View the dashboard',
              'Add 3 suppliers',
              'View resilience report',
            ]}
            loginStreak={loginStreak}
          />
        </Grid>
      </Grid>
      
      {/* KPI Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Resilience Score
              </Typography>
              {isLoadingResilience ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <Typography variant="h4">
                    {resilienceScore?.overall_resilience_score.toFixed(2)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="success.main">
                      +0.05 from last month
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Carbon Footprint
              </Typography>
              {isLoadingSustainability ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <Typography variant="h4">
                    {sustainabilityMetrics?.carbon_footprint.total_emissions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {sustainabilityMetrics?.carbon_footprint.unit}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingDownIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="success.main">
                      {sustainabilityMetrics?.carbon_footprint.trend}% reduction
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Active Disruptions
              </Typography>
              {isLoadingDisruptions ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <Typography variant="h4">
                    {activeDisruptions?.length || 0}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <WarningIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="error.main">
                      {activeDisruptions && activeDisruptions.length > 0 
                        ? 'Requires attention' 
                        : 'No active disruptions'}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Recommendations
              </Typography>
              {isLoadingRecommendations ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <Typography variant="h4">
                    {recommendations?.length || 0}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <InfoIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="info.main">
                      {recommendations && recommendations.length > 0 
                        ? `${recommendations.filter(r => r.priority === 'High').length} high priority` 
                        : 'No recommendations'}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Main Dashboard Content */}
      <Grid container spacing={3}>
        {/* Resilience Radar Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Resilience Analysis" />
            <Divider />
            <CardContent sx={{ height: 300 }}>
              {isLoadingResilience ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : getResilienceRadarData().length > 0 ? (
                <ResponsiveRadar
                  data={getResilienceRadarData()}
                  keys={['value']}
                  indexBy="metric"
                  maxValue={1}
                  margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                  borderColor={{ from: 'color' }}
                  gridLabelOffset={36}
                  dotSize={10}
                  dotColor={{ theme: 'background' }}
                  dotBorderWidth={2}
                  colors={{ scheme: 'nivo' }}
                  blendMode="multiply"
                  motionConfig="wobbly"
                />
              ) : (
                <Alert severity="info">No resilience data available.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Carbon Footprint Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Carbon Footprint Breakdown" />
            <Divider />
            <CardContent sx={{ height: 300 }}>
              {isLoadingSustainability ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : getCarbonFootprintData().length > 0 ? (
                <ResponsivePie
                  data={getCarbonFootprintData()}
                  margin={{ top: 40, right: 80, bottom: 40, left: 80 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  colors={{ scheme: 'nivo' }}
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                />
              ) : (
                <Alert severity="info">No carbon footprint data available.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Active Disruptions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Active Disruptions" 
              action={
                <Button variant="outlined" size="small">
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {isLoadingDisruptions ? (
                <LinearProgress />
              ) : activeDisruptions && activeDisruptions.length > 0 ? (
                <List>
                  {activeDisruptions.slice(0, 3).map((disruption) => (
                    <ListItem key={disruption.id} alignItems="flex-start">
                      <ListItemIcon>
                        <WarningIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">{disruption.description}</Typography>
                            <Chip 
                              label={disruption.severity >= 0.7 ? 'High' : 'Medium'} 
                              color={disruption.severity >= 0.7 ? 'error' : 'warning'} 
                              size="small" 
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="textSecondary">
                              Expected recovery: {disruption.expected_end_date}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Impact: {disruption.impact.financial_impact}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="success">No active disruptions at this time.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Top Recommendations */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Top Recommendations" 
              action={
                <Button variant="outlined" size="small">
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {isLoadingRecommendations ? (
                <LinearProgress />
              ) : recommendations && recommendations.length > 0 ? (
                <List>
                  {recommendations.slice(0, 3).map((recommendation) => (
                    <ListItem key={recommendation.id} alignItems="flex-start">
                      <ListItemIcon>
                        <InfoIcon color={recommendation.priority === 'High' ? 'error' : 'info'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">{recommendation.title}</Typography>
                            <Chip 
                              label={recommendation.priority} 
                              color={recommendation.priority === 'High' ? 'error' : 'info'} 
                              size="small" 
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="textSecondary">
                              {recommendation.description}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Confidence: {(recommendation.confidence * 100).toFixed(0)}%
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No recommendations at this time.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Agent Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Agent Status" />
            <Divider />
            <CardContent>
              {isLoadingAgentStatus ? (
                <LinearProgress />
              ) : agentStatusError ? (
                <Alert severity="error">Error loading agent status</Alert>
              ) : agentStatus ? (
                <List>
                  {Object.entries(agentStatus).map(([agentName, status]: [string, any]) => (
                    <ListItem key={agentName}>
                      <ListItemIcon>
                        {status.status === 'active' ? (
                          <CheckCircleIcon color="success" />
                        ) : status.status === 'idle' ? (
                          <InfoIcon color="info" />
                        ) : (
                          <WarningIcon color="warning" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">{agentName}</Typography>
                            <Chip
                              label={status.status}
                              color={
                                status.status === 'active' ? 'success' :
                                status.status === 'idle' ? 'info' : 'warning'
                              }
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          status.current_task ? (
                            <Typography variant="body2" color="textSecondary">
                              Working on: {status.current_task}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Last active: {new Date(status.last_active).toLocaleString()}
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No agent status available</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Task Queue */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Task Queue" 
              subheader={taskQueue ? `${taskQueue.queue_size} pending, ${taskQueue.active_tasks} active` : ''}
            />
            <Divider />
            <CardContent>
              {isLoadingTaskQueue ? (
                <LinearProgress />
              ) : taskQueueError ? (
                <Alert severity="error">Error loading task queue</Alert>
              ) : taskQueue && taskQueue.tasks && taskQueue.tasks.length > 0 ? (
                <List>
                  {taskQueue.tasks.map((task: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={task.type || 'Unknown task'}
                        secondary={
                          <>
                            <Typography variant="body2" color="textSecondary">
                              Priority: {task.priority || 'Normal'}
                            </Typography>
                            {task.start_time && (
                              <Typography variant="body2" color="textSecondary">
                                Started: {new Date(task.start_time).toLocaleString()}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No tasks in queue</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Recent Activity" />
            <Divider />
            <CardContent>
              {isLoadingTaskHistory ? (
                <LinearProgress />
              ) : taskHistoryError ? (
                <Alert severity="error">Error loading task history</Alert>
              ) : taskHistory && taskHistory.length > 0 ? (
                <List>
                  {taskHistory.map((task: any) => (
                    <ListItem key={task.task_id}>
                      <ListItemIcon>
                        {task.status === 'completed' ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <InfoIcon color="info" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={task.task_type}
                        secondary={
                          <>
                            <Typography variant="body2" color="textSecondary">
                              Status: {task.status}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {new Date(task.timestamp).toLocaleString()}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No recent activity</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Memory Stats */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Memory System" />
            <Divider />
            <CardContent>
              {isLoadingMemoryStats ? (
                <LinearProgress />
              ) : memoryStatsError ? (
                <Alert severity="error">Error loading memory stats</Alert>
              ) : memoryStats ? (
                <Box>
                  <Typography variant="body1">
                    Total Items: {memoryStats.total_items || 0}
                  </Typography>
                  <Typography variant="body1">
                    Memory Usage: {memoryStats.memory_usage || '0 MB'}
                  </Typography>
                  <Typography variant="body1">
                    Last Updated: {memoryStats.last_updated ? new Date(memoryStats.last_updated).toLocaleString() : 'Never'}
                  </Typography>
                  {memoryStats.message && (
                    <Alert severity="info" sx={{ mt: 2 }}>{memoryStats.message}</Alert>
                  )}
                </Box>
              ) : (
                <Alert severity="info">No memory stats available</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Carbon Footprint Trend */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Carbon Footprint Trend" />
            <Divider />
            <CardContent sx={{ height: 300 }}>
              {isLoadingSustainability ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : sustainabilityMetrics?.carbon_footprint.historical ? (
                <ResponsiveLine
                  data={getCarbonTrendData()}
                  margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{
                    type: 'linear',
                    min: 'auto',
                    max: 'auto',
                    stacked: false,
                    reverse: false
                  }}
                  yFormat=" >-.2f"
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Period',
                    legendOffset: 36,
                    legendPosition: 'middle'
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Carbon Emissions',
                    legendOffset: -40,
                    legendPosition: 'middle'
                  }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  legends={[
                    {
                      anchor: 'bottom-right',
                      direction: 'column',
                      justify: false,
                      translateX: 100,
                      translateY: 0,
                      itemsSpacing: 0,
                      itemDirection: 'left-to-right',
                      itemWidth: 80,
                      itemHeight: 20,
                      itemOpacity: 0.75,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                      effects: [
                        {
                          on: 'hover',
                          style: {
                            itemBackground: 'rgba(0, 0, 0, .03)',
                            itemOpacity: 1
                          }
                        }
                      ]
                    }
                  ]}
                />
              ) : (
                <Alert severity="info">No historical data available.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
